import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { paymentsApi, type PixChargeOut } from '@/api/payments';
import {
  walletApi,
  walletNumbers,
  type WalletOut,
  type WalletTransaction
} from '@/api/wallet';
import {
  promotionsApi,
  selectTier,
  tierName,
  methodFactor,
  type CashbackRuleOut,
  type CashbackTier
} from '@/api/promotions';
import { getMe, type MeOut } from '@/api/alianca';
import CardStep from './recharge/CardStep';
import { useWalletStore } from '@/store/wallet';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Display, Eyebrow, MoneyDisplay } from '@/components/ui/Typography';
import { effectiveCashback, tierByKey } from '@/lib/alianca-tiers';
import { formatBRL } from '@/lib/format';
import { friendlyMessage } from '@/lib/errors';

const PIX_MIN = 5;
const PIX_MAX = 50000;
const BALANCE_CAP = 50000;
const POLL_INTERVAL_MS = 4000;

/** Tints dos cards turbo, por ordem das faixas do admin (T10). */
const TURBO_TINTS = ['#FFB92A', '#0090E2', '#8345BA'];

interface Suggestion {
  value: number;
  pct: number;
}

type Phase = 'value' | 'payment' | 'card' | 'qr' | 'paid' | 'expired';
type PayMethod = 'pix' | 'card';
const SHOW_DEV_TOOLS = import.meta.env.VITE_SHOW_DEV_TOOLS === '1';

export default function RechargePix() {
  const navigate = useNavigate();
  const location = useLocation();
  // Origem do fluxo "sem saldo → adicionar crédito" (ConfigureCharge ou
  // VacuumOffer mandam `state.from`). Depois do PIX pago, o CTA primário
  // devolve o usuário pra onde ele parou (feedback Saulo 2026-07-04).
  const navState = location.state as { from?: string; intent?: string } | null;
  const returnTo = navState?.from ?? null;
  // "Adicionar cartão" (Cartões salvos) cai aqui: o PagBank só tokeniza numa
  // cobrança, então salvar o cartão = 1º aporte com ele (docs/bugs/2026-10-08).
  const addingCard = navState?.intent === 'add_card';
  const storedWallet = useWalletStore((s) => s.wallet);
  const setStoredWallet = useWalletStore((s) => s.setWallet);

  const [phase, setPhase] = useState<Phase>('value');
  const [method, setMethod] = useState<PayMethod>(addingCard ? 'card' : 'pix');
  // Cartão só é selecionável quando o backend liga a flag (GET /payments/methods).
  // Off = tile "em breve" (comportamento de hoje), sem republicar bundle.
  const [cardEnabled, setCardEnabled] = useState(false);
  // Mínimo do cartão (R$ 20 no dia 1, > mínimo do PIX). O backend é a fonte
  // (card_min_brl no GET /payments/methods); barra ANTES do POST pra não expor
  // o "valor fora do intervalo" genérico do gateway.
  const [cardMin, setCardMin] = useState(20);
  // Zerado de propósito: cliente escolhe o valor (feedback 2026-07-02 — o
  // default de R$ 500 "dava um susto"; e o select() de valor pré-preenchido
  // no focus não funciona no iOS, que só acrescentava dígitos ao 500).
  const [amount, setAmount] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [charge, setCharge] = useState<PixChargeOut | null>(null);
  // null = ainda nao lido (fetch inicial falhou) — o QrPhase estabelece
  // o baseline no primeiro poll em vez de assumir 0.
  const [initialBalance, setInitialBalance] = useState<number | null>(null);
  const [paidWallet, setPaidWallet] = useState<WalletOut | null>(null);
  const [cashbackRule, setCashbackRule] = useState<CashbackRuleOut | null>(null);
  const [me, setMe] = useState<MeOut | null>(null);
  const [paidBreakdown, setPaidBreakdown] = useState<{
    pixCredit: number;
    cashback: number;
    cashbackPercent: number | null;
  } | null>(null);

  useEffect(() => {
    promotionsApi
      .getActiveCashbackRule()
      .then((r) => setCashbackRule(r))
      .catch(() => {
        /* sem regra ativa é ok — UI cai no default sem preview */
      });
    getMe()
      .then(setMe)
      .catch(() => {
        /* sem aliança → só escala Wallet */
      });
  }, []);

  useEffect(() => {
    walletApi
      .get()
      .then((w) => {
        setStoredWallet(w);
        setInitialBalance(Number(w.balance_brl));
      })
      .catch(() => {
        /* saldo inicial é best-effort */
      });
    paymentsApi
      .getMethods()
      .then((ms) => {
        const card = ms.find((m) => m.method === 'credit_card');
        setCardEnabled(!!card?.enabled);
        if (card?.min_brl != null) setCardMin(Number(card.min_brl));
        // Cartão pré-escolhido (addingCard) mas desligado → PIX, senão o
        // cliente fica num tile "em breve" selecionado.
        if (!card?.enabled) setMethod('pix');
      })
      .catch(() => {
        /* sem a rota (backend antigo) → cartão fica "em breve" */
        setMethod('pix');
      });
  }, [setStoredWallet]);

  const wn = walletNumbers(storedWallet);
  // Mínimo dinâmico do aporte: quando devedor, o backend manda a dívida em
  // min_topup_brl; sem o campo, cai na dívida local ou no piso de R$ 5.
  const minTopup =
    storedWallet?.min_topup_brl != null
      ? Number(storedWallet.min_topup_brl)
      : wn.debt > 0
        ? wn.debt
        : PIX_MIN;
  // O teto do backend vale sobre saldo + cashback (as duas carteiras somadas),
  // então o espaço restante desconta os dois — não só o saldo.
  const availableRoom = BALANCE_CAP - (wn.balance + wn.bonus);
  const maxAmount = Math.min(PIX_MAX, Math.floor(availableRoom));
  const tooMuch = amount > maxAmount;
  const tooLittle = amount < minTopup;
  // Cartão tem piso próprio (R$ 20) acima do PIX — só morde quando o método é
  // cartão (o passo de valor segue liberando o mínimo do PIX pro trilho PIX).
  const belowCardMin = method === 'card' && amount < cardMin;
  const canSubmit = !submitting && !tooMuch && !tooLittle && !belowCardMin;

  const walletTiers: CashbackTier[] = cashbackRule?.tiers ?? [];
  const aliancaAtiva = !!me?.membership?.ativa;
  const aliancaPct = aliancaAtiva ? me!.membership!.bonus_alianca_pct : 0;
  const nivel = aliancaAtiva ? tierByKey(me!.membership!.nivel) : null;

  // Fator do trilho: cartão no dia 1 tem fator 0 → cashback zero no preview
  // (espelha o backend). O preview escolhe "Cartão" só existe no passo 2.
  const payFactor =
    method === 'card' ? methodFactor(cashbackRule, 'credit_card') : 1;
  const cb = useMemo(
    () => effectiveCashback(amount, aliancaPct, walletTiers, payFactor),
    [amount, aliancaPct, walletTiers, payFactor]
  );

  // Cards turbo = faixas da regra do admin. Faixa cujo % não supera o tier
  // Aliança do cliente não agrega nada → some. Ordenadas asc.
  const suggestions: Suggestion[] = useMemo(
    () =>
      walletTiers
        .map((t) => ({ value: Number(t.min_brl), pct: t.percent }))
        .filter((s) => s.pct > aliancaPct)
        .sort((a, b) => a.value - b.value),
    [walletTiers, aliancaPct]
  );

  // Tint da faixa vencedora (pílula do passo 2): Aliança vence → cor do nível;
  // escala vence → tint (por ordem) do card turbo correspondente.
  const winnerTint = useMemo(() => {
    if (cb.source === 'alianca' && nivel) return nivel.hex;
    const tier = selectTier(amount, walletTiers);
    if (!tier) return TURBO_TINTS[TURBO_TINTS.length - 1];
    const idx = suggestions.findIndex((s) => s.value === Number(tier.min_brl));
    return TURBO_TINTS[Math.min(idx < 0 ? TURBO_TINTS.length - 1 : idx, TURBO_TINTS.length - 1)];
  }, [cb.source, nivel, amount, walletTiers, suggestions]);

  // CTA do passo 2: PIX gera a cobrança e vai pro QR; cartão vai pro passo 3.
  async function onSubmitPayment() {
    if (method === 'card') {
      setError(null);
      setPhase('card');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await paymentsApi.createPix(amount);
      setCharge(created);
      setPhase('qr');
    } catch (err) {
      setError(friendlyMessage(err, 'Falha ao gerar PIX'));
    } finally {
      setSubmitting(false);
    }
  }

  const handlePaid = useCallback(
    async (wallet: WalletOut, opts?: { skipBreakdown?: boolean }) => {
      setStoredWallet(wallet);
      setPaidWallet(wallet);
      setPhase('paid');
      // Cartão = fator 0 (sem cashback): não roda o heurístico de transações,
      // que buscaria a última `credit_pix` e casaria com um PIX ANTERIOR + o
      // cashback dele (bug de display do sucesso do cartão). O reset explícito
      // zera um breakdown deixado por um PIX no mesmo mount.
      if (opts?.skipBreakdown) {
        setPaidBreakdown(null);
        return;
      }
      try {
        const txs = await walletApi.transactions(5, 0);
        const breakdown = extractPaymentBreakdown(txs.items);
        if (breakdown) setPaidBreakdown(breakdown);
      } catch {
        /* best-effort */
      }
    },
    [setStoredWallet]
  );

  const handleExpired = useCallback(() => setPhase('expired'), []);
  // Cancelar/regerar volta pro passo 2 (valor escolhido preservado).
  const handleCancel = useCallback(() => {
    setCharge(null);
    setPhase('payment');
  }, []);

  if (phase === 'value') {
    return (
      <T10Valor
        amount={amount}
        setAmount={setAmount}
        balance={wn.balance}
        minTopup={minTopup}
        maxAmount={maxAmount}
        tooLittle={tooLittle}
        tooMuch={tooMuch}
        suggestions={suggestions}
        aliancaAtiva={aliancaAtiva}
        aliancaPct={aliancaPct}
        onContinue={() => setPhase('payment')}
        onBack={() => navigate(-1)}
      />
    );
  }

  if (phase === 'payment') {
    return (
      <T10Pagamento
        amount={amount}
        debt={wn.debt}
        cbPct={cb.value > 0 ? cb.pct : 0}
        cbValue={cb.value}
        winnerTint={winnerTint}
        method={method}
        cardEnabled={cardEnabled}
        cardMinBrl={cardMin}
        onMethod={setMethod}
        error={error}
        canSubmit={canSubmit}
        submitting={submitting}
        onSubmit={onSubmitPayment}
        onBack={() => setPhase('value')}
      />
    );
  }

  if (phase === 'card') {
    return (
      <CardStep
        amount={amount}
        cashbackValue={cb.value}
        onPaid={(w) => handlePaid(w, { skipBreakdown: true })}
        onBack={() => setPhase('payment')}
        onFallbackToPix={() => {
          setMethod('pix');
          setPhase('payment');
        }}
        fallbackWallet={storedWallet}
        onDone={() => navigate('/wallet')}
        showDevTools={SHOW_DEV_TOOLS}
        startWithNewCard={addingCard}
        defaultStoreCard={addingCard}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="pt-1">
        <Eyebrow>Adicionar saldo</Eyebrow>
        <Display size={28} className="mt-1">
          Recarregar
        </Display>
      </header>

      {phase === 'qr' && charge && (
        <QrPhase
          charge={charge}
          initialBalance={initialBalance}
          onPaid={handlePaid}
          onExpired={handleExpired}
          onCancel={handleCancel}
        />
      )}

      {phase === 'paid' && paidWallet && (
        <PaidPhase
          wallet={paidWallet}
          method={method}
          initialBalance={initialBalance}
          breakdown={paidBreakdown}
          returnTo={returnTo}
          onContinue={returnTo ? () => navigate(returnTo) : undefined}
          onBack={() => navigate('/')}
          onViewWallet={() => navigate('/wallet')}
        />
      )}

      {phase === 'expired' && (
        <ExpiredPhase
          onRetry={() => {
            setCharge(null);
            setPhase('payment');
          }}
        />
      )}
    </div>
  );
}

/* ============================================================
   Frame NSS + header dos passos (telas claras simples, T10)
   ============================================================ */

/**
 * Frame de altura fixa (100dvh − BottomNav) com overflow-hidden — mantém a
 * garantia sem-scroll da rota. Cancela o padding do AppLayout e reaplica a
 * safe-area por conta própria. Com teclado aberto, a altura vem do
 * visualViewport (iOS não encolhe o dvh) pro CTA ficar acima do teclado.
 */
export function StepFrame({
  kbHeight,
  children
}: {
  kbHeight: number | null;
  children: React.ReactNode;
}) {
  return (
    <div
      className="-mx-4 -mb-24 flex flex-col overflow-hidden px-5"
      style={{
        marginTop: 'calc(-1 * max(1rem, env(safe-area-inset-top)))',
        paddingTop: 'max(1rem, env(safe-area-inset-top))',
        height:
          kbHeight !== null
            ? `${kbHeight}px`
            : 'calc(100dvh - 62px - max(0.75rem, env(safe-area-inset-bottom)))'
      }}
    >
      {children}
    </div>
  );
}

export function StepHeader({
  title,
  step,
  onBack
}: {
  title: string;
  step: string;
  onBack: () => void;
}) {
  return (
    <div className="relative flex flex-shrink-0 items-center justify-between py-2">
      <button
        type="button"
        onClick={onBack}
        aria-label="Voltar"
        className="z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-bow-royal"
        style={{ boxShadow: '0 1px 2px rgba(0,27,116,0.08)' }}
      >
        <ChevronLeftIcon />
      </button>
      <span
        className="pointer-events-none absolute inset-x-0 text-center text-bow-royal"
        style={{
          fontFamily: 'Chillax, system-ui, sans-serif',
          fontWeight: 600,
          fontSize: 19,
          letterSpacing: '-0.02em'
        }}
      >
        {title}
      </span>
      <span
        className="z-10 rounded-full px-2.5 py-1 text-[11px] font-semibold"
        style={{ background: 'rgba(0,27,116,0.08)', color: 'rgba(0,27,116,0.6)' }}
      >
        {step}
      </span>
    </div>
  );
}

function RadioDot({
  checked,
  tint = '#001B74',
  size = 16
}: {
  checked: boolean;
  tint?: string;
  size?: number;
}) {
  return (
    <span
      aria-hidden
      className="inline-flex flex-shrink-0 items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        border: checked ? 'none' : '1.5px solid rgba(0,27,116,0.25)',
        background: checked ? tint : '#FFFFFF'
      }}
    >
      {checked && (
        <span
          className="rounded-full"
          style={{ width: size * 0.35, height: size * 0.35, background: '#FFFFFF' }}
        />
      )}
    </span>
  );
}

/* ============================================================
   Passo 1/2 — T10Valor
   ============================================================ */

interface T10ValorProps {
  amount: number;
  setAmount: (n: number) => void;
  balance: number;
  minTopup: number;
  maxAmount: number;
  tooLittle: boolean;
  tooMuch: boolean;
  suggestions: Suggestion[];
  aliancaAtiva: boolean;
  aliancaPct: number;
  onContinue: () => void;
  onBack: () => void;
}

const PRESETS = [50, 75, 100];

function T10Valor({
  amount,
  setAmount,
  balance,
  minTopup,
  maxAmount,
  tooLittle,
  tooMuch,
  suggestions,
  aliancaAtiva,
  aliancaPct,
  onContinue,
  onBack
}: T10ValorProps) {
  const [outroOpen, setOutroOpen] = useState(false);
  const [kbOpen, setKbOpen] = useState(false);
  const [vvHeight, setVvHeight] = useState<number | null>(null);
  const outroRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!kbOpen) {
      setVvHeight(null);
      return;
    }
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setVvHeight(Math.round(vv.height));
    update();
    vv.addEventListener('resize', update);
    return () => vv.removeEventListener('resize', update);
  }, [kbOpen]);

  useEffect(() => {
    if (outroOpen) outroRef.current?.focus();
  }, [outroOpen]);

  function pick(v: number) {
    setOutroOpen(false);
    setAmount(v);
  }

  const outroSelected = outroOpen;
  const belowMin = amount > 0 && tooLittle;
  const canContinue = amount > 0 && !tooLittle && !tooMuch;
  const devedor = balance < 0;

  return (
    <StepFrame kbHeight={kbOpen ? vvHeight : null}>
      <StepHeader title="Adicionar saldo" step="1/2" onBack={onBack} />

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pt-1 [@media(max-height:740px)]:gap-2.5">
        {/* Saldo atual */}
        <div className="flex flex-shrink-0 items-center justify-between rounded-2xl bg-white px-4 py-3.5 [@media(max-height:740px)]:py-3">
          <Eyebrow>Saldo atual</Eyebrow>
          <span
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 600,
              fontSize: 19,
              letterSpacing: '-0.02em',
              lineHeight: 1,
              color: devedor ? '#EE263E' : '#001B74'
            }}
          >
            {devedor ? `− ${formatBRL(Math.abs(balance))}` : formatBRL(balance)}
          </span>
        </div>

        {/* Escolha um valor */}
        <div className="flex flex-shrink-0 flex-col gap-2">
          <Eyebrow>Escolha um valor</Eyebrow>
          <div className="grid grid-cols-3 gap-2">
            {PRESETS.map((v) => {
              const sel = !outroOpen && amount === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => pick(v)}
                  className="flex h-[56px] items-center justify-center gap-2 rounded-2xl transition [@media(max-height:740px)]:h-[48px]"
                  style={{
                    background: sel ? 'rgba(0,27,116,0.04)' : '#FFFFFF',
                    border: sel
                      ? '1.5px solid #001B74'
                      : '1px solid rgba(0,27,116,0.14)'
                  }}
                >
                  <RadioDot checked={sel} size={14} />
                  <span className="font-display text-[16px] font-semibold tracking-[-0.015em] text-bow-royal">
                    R${v}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Cards turbo — faixas do admin, tint por ordem */}
          {suggestions.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {suggestions.map((s, i) => {
                const tint = TURBO_TINTS[Math.min(i, TURBO_TINTS.length - 1)];
                const sel = !outroOpen && amount === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    disabled={s.value > maxAmount}
                    onClick={() => pick(s.value)}
                    className="flex flex-col items-center gap-1 rounded-2xl px-1 py-2.5 transition disabled:opacity-40 [@media(max-height:740px)]:py-2"
                    style={{
                      background: sel ? `${tint}0D` : '#FFFFFF',
                      border: sel ? `1.5px solid ${tint}` : `1px solid ${tint}33`
                    }}
                  >
                    <RadioDot checked={sel} tint={tint} size={14} />
                    <span className="font-display text-[17px] font-semibold tracking-[-0.015em] text-bow-royal">
                      R$ {s.value.toLocaleString('pt-BR')}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      {aliancaAtiva ? (
                        <>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: 'rgba(0,27,116,0.4)'
                            }}
                          >
                            {aliancaPct}%
                          </span>
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke={tint}
                            strokeWidth="2.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M5 12h14M13 5l7 7-7 7" />
                          </svg>
                          <span
                            style={{ fontSize: 12, fontWeight: 700, color: tint }}
                          >
                            {s.pct}%
                          </span>
                        </>
                      ) : (
                        <span
                          style={{ fontSize: 12, fontWeight: 700, color: tint }}
                        >
                          +{s.pct}%
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Outro valor */}
        {outroOpen ? (
          <div className="flex flex-shrink-0 flex-col gap-1.5">
            <div
              className="flex h-[52px] items-center gap-2.5 rounded-2xl bg-white px-4"
              style={{
                border: belowMin ? '1.5px solid #D5152B' : '1.5px solid #001B74',
                boxShadow: kbOpen ? '0 0 0 3px rgba(0,27,116,0.08)' : 'none'
              }}
            >
              <PencilIcon />
              <span className="text-[13px] font-medium text-bow-royal/60">
                R$
              </span>
              <input
                ref={outroRef}
                type="number"
                inputMode="decimal"
                value={amount > 0 ? amount : ''}
                placeholder="0"
                min={minTopup}
                max={maxAmount}
                step={1}
                onFocus={(e) => {
                  setKbOpen(true);
                  // iOS WebView ignora select() síncrono no focus (Android
                  // não); o retry no próximo frame cobre refoco com valor.
                  const el = e.currentTarget;
                  el.select();
                  requestAnimationFrame(() => el.select());
                }}
                onBlur={() => setKbOpen(false)}
                onChange={(e) => {
                  const v = e.target.value;
                  setAmount(v === '' ? 0 : Number(v));
                }}
                className="min-w-0 flex-1 bg-transparent font-display text-[17px] font-semibold tracking-[-0.015em] text-bow-royal focus:outline-none"
              />
            </div>
            {belowMin && (
              <p className="px-1 text-[12.5px]" style={{ color: '#D5152B' }}>
                Mínimo {formatBRL(minTopup)}
              </p>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setAmount(0);
              setOutroOpen(true);
            }}
            className="flex h-[52px] flex-shrink-0 items-center justify-center gap-2 rounded-2xl bg-white text-bow-royal [@media(max-height:740px)]:h-[46px]"
            style={{ border: '1px dashed rgba(0,27,116,0.3)' }}
          >
            <PencilIcon />
            <span className="font-display text-[15px] font-semibold tracking-[-0.015em]">
              Outro valor
            </span>
          </button>
        )}

        {/* Mínimo violado por preset/turbo (devedor com dívida > valor) */}
        {belowMin && !outroSelected && (
          <p className="flex-shrink-0 px-1 text-[12.5px]" style={{ color: '#D5152B' }}>
            Mínimo {formatBRL(minTopup)}
          </p>
        )}
        {tooMuch && (
          <p className="flex-shrink-0 px-1 text-[12.5px] text-bow-red">
            Máximo permitido agora: {formatBRL(maxAmount)} (limite de saldo{' '}
            {formatBRL(BALANCE_CAP)}).
          </p>
        )}
      </div>

      {/* CTA ancorado no rodapé (NSS). onPointerDown preventDefault: com o
          teclado aberto, o tap não pode blur-ar o input antes do click. */}
      <div className="flex-shrink-0 pb-2.5 pt-2.5 [@media(max-height:740px)]:pt-2">
        <Button
          onClick={onContinue}
          disabled={!canContinue}
          fullWidth
          size="lg"
          onPointerDown={(e) => e.preventDefault()}
        >
          Continuar
        </Button>
      </div>
    </StepFrame>
  );
}

/* ============================================================
   Passo 2/2 — T10Pagamento
   ============================================================ */

interface T10PagamentoProps {
  amount: number;
  debt: number;
  cbPct: number;
  cbValue: number;
  winnerTint: string;
  method: PayMethod;
  cardEnabled: boolean;
  cardMinBrl: number;
  onMethod: (m: PayMethod) => void;
  error: string | null;
  canSubmit: boolean;
  submitting: boolean;
  onSubmit: () => void;
  onBack: () => void;
}

export function T10Pagamento({
  amount,
  debt,
  cbPct,
  cbValue,
  winnerTint,
  method,
  cardEnabled,
  cardMinBrl,
  onMethod,
  error,
  canSubmit,
  submitting,
  onSubmit,
  onBack
}: T10PagamentoProps) {
  const isCard = method === 'card';
  // Cartão tem piso próprio (R$ 20). Abaixo dele, avisa e trava o CTA (o
  // canSubmit já vem falso do pai) — em vez de deixar o gateway recusar com
  // "valor fora do intervalo permitido".
  const belowCardMin = isCard && amount < cardMinBrl;
  return (
    <StepFrame kbHeight={null}>
      <StepHeader title="Pagamento" step="2/2" onBack={onBack} />

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pt-1 [@media(max-height:740px)]:gap-2.5">
        {/* Valor + pílula do cashback vencedor (some no cartão: fator 0) */}
        <div className="flex flex-shrink-0 items-center justify-between rounded-2xl bg-white px-4 py-3.5">
          <MoneyDisplay value={amount} size={30} />
          {cbPct > 0 && (
            <span
              className="rounded-full px-2.5 py-1 text-[11px] font-bold"
              style={{ background: `${winnerTint}1A`, color: winnerTint }}
            >
              {cbPct}% cashback
            </span>
          )}
        </div>

        {/* Resultado nos bolsos. No cartão o card de cashback some e o saldo
            recebe o valor cheio — reflete o crédito real (cashback zero). */}
        {isCard ? (
          <div className="rounded-2xl bg-white px-4 py-3.5 shadow-soft">
            <Eyebrow>Entra no saldo</Eyebrow>
            <div
              className="mt-1"
              style={{
                fontFamily: 'Chillax, system-ui, sans-serif',
                fontWeight: 600,
                fontSize: 22,
                letterSpacing: '-0.03em',
                color: '#00A664'
              }}
            >
              {formatBRL(amount - debt)}
            </div>
          </div>
        ) : (
          <WalletResultCards
            saldoValue={formatBRL(amount - debt)}
            saldoSub={debt > 0 ? 'já descontado o negativo' : undefined}
            cashValue={`+ ${formatBRL(cbValue)}`}
            cashSub="separada do saldo"
          />
        )}

        {/* Pagar com — PIX | Cartão. Cartão só selecionável com a flag. */}
        <div className="flex flex-shrink-0 flex-col gap-2">
          <Eyebrow>Pagar com</Eyebrow>
          <div className="grid grid-cols-2 gap-2">
            <MethodTile
              selected={!isCard}
              tint="#00A664"
              onClick={() => onMethod('pix')}
              icon={<PixDiamonds size={34} />}
              label="PIX"
              badge={
                cbPct > 0 ? (
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                    style={{ background: 'rgba(0,166,100,0.10)', color: '#00A664' }}
                  >
                    +{cbPct}% cashback
                  </span>
                ) : undefined
              }
            />
            {cardEnabled ? (
              <MethodTile
                selected={isCard}
                tint="#001B74"
                onClick={() => onMethod('card')}
                icon={<CardIcon />}
                label="Cartão"
                badge={
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.03em]"
                    style={{ background: 'rgba(0,27,116,0.07)', color: 'rgba(0,27,116,0.55)' }}
                  >
                    sem cashback
                  </span>
                }
              />
            ) : (
              <div
                className="flex flex-col items-center justify-center gap-1.5 rounded-2xl bg-white py-3.5 opacity-50 [@media(max-height:740px)]:py-3"
                style={{ border: '1px solid rgba(0,27,116,0.12)' }}
              >
                <CardIcon />
                <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-bow-royal">
                  Cartão
                  <span
                    className="rounded-full px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-[0.05em]"
                    style={{ background: 'rgba(0,27,116,0.08)', color: 'rgba(0,27,116,0.55)' }}
                  >
                    em breve
                  </span>
                </span>
              </div>
            )}
          </div>
          {belowCardMin ? (
            <p className="text-[11.5px] font-semibold text-bow-red">
              Valor mínimo de {formatBRL(cardMinBrl)} no cartão. Aumente o valor ou pague com PIX.
            </p>
          ) : (
            <p className="text-[11.5px]" style={{ color: 'rgba(0,27,116,0.55)' }}>
              {isCard
                ? 'Cobrança única no cartão. O cashback é benefício do PIX.'
                : 'Aprovação em segundos — o saldo entra na hora.'}
            </p>
          )}
        </div>

        {error && (
          <p className="flex-shrink-0 px-1 text-[12.5px] text-bow-red">{error}</p>
        )}
      </div>

      <div className="flex-shrink-0 pb-2.5 pt-2.5 [@media(max-height:740px)]:pt-2">
        <Button onClick={onSubmit} disabled={!canSubmit} fullWidth size="lg">
          {isCard
            ? `Pagar no cartão · ${formatBRL(amount)}`
            : submitting
              ? 'Gerando PIX…'
              : `Gerar PIX · ${formatBRL(amount)}`}
        </Button>
      </div>
    </StepFrame>
  );
}

function MethodTile({
  selected,
  tint,
  onClick,
  icon,
  label,
  badge
}: {
  selected: boolean;
  tint: string;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex flex-col items-center justify-center gap-1.5 rounded-2xl bg-white py-3.5 [@media(max-height:740px)]:py-3"
      style={{ border: `1.5px solid ${selected ? tint : 'rgba(0,27,116,0.12)'}` }}
    >
      {selected && (
        <span
          className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full text-white"
          style={{ background: tint }}
        >
          <CheckIcon />
        </span>
      )}
      <span style={{ color: tint }}>{icon}</span>
      <span className="text-[13px] font-semibold text-bow-royal">{label}</span>
      {badge}
    </button>
  );
}

/**
 * Par de cards Carteira saldo / Carteira cashback — usado no passo 2
 * (o que entra em cada bolso) e no PaidPhase (totais pós-pagamento).
 */
function WalletResultCards({
  saldoValue,
  saldoSub,
  cashValue,
  cashSub
}: {
  saldoValue: string;
  saldoSub?: string;
  cashValue: string;
  cashSub?: string;
}) {
  return (
    <div className="grid flex-shrink-0 grid-cols-2 gap-2">
      <div
        className="rounded-2xl px-3.5 py-3"
        style={{
          background: 'rgba(0,192,114,0.06)',
          border: '1px solid rgba(0,192,114,0.28)'
        }}
      >
        <span
          className="text-[10px] font-bold uppercase tracking-[0.08em]"
          style={{ color: '#00834D' }}
        >
          Carteira saldo
        </span>
        <div
          className="mt-1"
          style={{
            fontFamily: 'Chillax, system-ui, sans-serif',
            fontWeight: 600,
            fontSize: 22,
            letterSpacing: '-0.025em',
            lineHeight: 1.05,
            color: '#00834D'
          }}
        >
          {saldoValue}
        </div>
        {saldoSub && (
          <div className="mt-1 text-[11px]" style={{ color: 'rgba(0,27,116,0.55)' }}>
            {saldoSub}
          </div>
        )}
      </div>
      <div
        className="rounded-2xl px-3.5 py-3"
        style={{
          background: 'rgba(192,76,174,0.05)',
          border: '1px solid rgba(192,76,174,0.28)'
        }}
      >
        <span
          className="text-[10px] font-bold uppercase tracking-[0.08em]"
          style={{ color: '#A23C92' }}
        >
          Carteira cashback
        </span>
        <div
          className="mt-1"
          style={{
            fontFamily: 'Chillax, system-ui, sans-serif',
            fontWeight: 600,
            fontSize: 22,
            letterSpacing: '-0.025em',
            lineHeight: 1.05,
            color: '#A23C92'
          }}
        >
          {cashValue}
        </div>
        {cashSub && (
          <div className="mt-1 text-[11px]" style={{ color: 'rgba(0,27,116,0.55)' }}>
            {cashSub}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Phases QR / Expired — inalteradas. Paid — resumo em 2 carteiras.
   ============================================================ */

interface QrPhaseProps {
  charge: PixChargeOut;
  initialBalance: number | null;
  onPaid: (wallet: WalletOut) => void;
  onExpired: () => void;
  onCancel: () => void;
}

function QrPhase({ charge, initialBalance, onPaid, onExpired, onCancel }: QrPhaseProps) {
  const expiresAt = new Date(charge.expires_at).getTime();
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
  );
  const [copied, setCopied] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simulateError, setSimulateError] = useState<string | null>(null);
  const settledRef = useRef(false);

  const showDevTools = import.meta.env.VITE_SHOW_DEV_TOOLS === '1';

  async function onSimulatePayment() {
    if (settledRef.current) return;
    setSimulating(true);
    setSimulateError(null);
    try {
      await paymentsApi.devSimulatePayment(charge.id);
      const wallet = await walletApi.get();
      if (!settledRef.current) {
        settledRef.current = true;
        onPaid(wallet);
      }
    } catch (err) {
      setSimulateError(friendlyMessage(err, 'Falha ao simular pagamento'));
      setSimulating(false);
    }
  }

  // Baseline do polling. `initialBalance` pode não ter sido lido (fetch
  // inicial falhou) — o default 0 prenderia em "Aguardando pagamento" o
  // devedor que paga exatamente a dívida e fecha em saldo 0 (0 > 0 é falso).
  // Nesse caso o primeiro poll bem-sucedido estabelece o baseline.
  const baselineRef = useRef<number | null>(
    initialBalance !== null ? initialBalance : null
  );

  useEffect(() => {
    const interval = window.setInterval(async () => {
      if (settledRef.current) return;
      try {
        const wallet = await walletApi.get();
        const balance = Number(wallet.balance_brl);
        if (baselineRef.current === null) {
          baselineRef.current = balance;
          return;
        }
        if (balance > baselineRef.current) {
          settledRef.current = true;
          onPaid(wallet);
        }
      } catch {
        /* rede instável — próxima tentativa cobre */
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [onPaid]);

  useEffect(() => {
    const tick = window.setInterval(() => {
      const left = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0 && !settledRef.current) {
        settledRef.current = true;
        window.clearInterval(tick);
        onExpired();
      }
    }, 1000);
    return () => window.clearInterval(tick);
  }, [expiresAt, onExpired]);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(charge.qr_code_text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <>
      <Card className="flex flex-col items-center gap-4 p-6">
        <Eyebrow>PIX de</Eyebrow>
        <MoneyDisplay value={Number(charge.amount_brl)} size={42} />
        {charge.qr_code_image_url ? (
          <img
            src={charge.qr_code_image_url}
            alt="QR PIX"
            className="h-56 w-56 rounded-2xl border border-bow-royal/10"
          />
        ) : (
          <div className="rounded-2xl border border-bow-royal/10 bg-white p-3">
            <QRCodeSVG
              value={charge.qr_code_text}
              size={200}
              bgColor="#FFFFFF"
              fgColor="#001B74"
              level="M"
            />
          </div>
        )}
        <span className="text-xs text-bow-royal/60">
          Expira em {formatCountdown(secondsLeft)}
        </span>
      </Card>

      <Card className="flex flex-col gap-2 p-4">
        <Eyebrow>PIX copia e cola</Eyebrow>
        <code className="break-all rounded-2xl bg-bow-royal/5 p-3 text-xs leading-relaxed">
          {charge.qr_code_text}
        </code>
        <Button variant="secondary" onClick={onCopy} size="md">
          {copied ? 'Copiado!' : 'Copiar código'}
        </Button>
      </Card>

      <Card className="flex items-center gap-2 bg-bow-royal/5 p-4">
        <span className="h-2 w-2 animate-pulse rounded-full bg-bow-blue" />
        <span className="text-sm text-bow-royal/75">
          Aguardando pagamento… o saldo aparece aqui automaticamente.
        </span>
      </Card>

      <Button variant="ghost" onClick={onCancel} size="md">
        Cancelar e gerar outro
      </Button>

      {showDevTools && (
        <div className="mt-2 flex flex-col gap-1 border-t border-dashed border-bow-royal/15 pt-3">
          <span className="text-[11px] uppercase tracking-wide text-bow-royal/40">
            Ferramentas de desenvolvimento
          </span>
          <button
            type="button"
            onClick={onSimulatePayment}
            disabled={simulating}
            className="h-9 rounded-full border border-bow-royal/15 bg-white/50 text-xs text-bow-royal/60 hover:border-bow-royal/30 hover:text-bow-royal disabled:opacity-50"
          >
            {simulating ? 'Simulando…' : 'Simular pagamento (dev)'}
          </button>
          {simulateError && (
            <span className="text-[11px] text-bow-red">{simulateError}</span>
          )}
        </div>
      )}
    </>
  );
}

interface PaidPhaseProps {
  wallet: WalletOut;
  method: PayMethod;
  initialBalance: number | null;
  breakdown: {
    pixCredit: number;
    cashback: number;
    cashbackPercent: number | null;
  } | null;
  /** Rota de origem do fluxo "sem saldo" — vira o CTA "continuar onde parei". */
  returnTo?: string | null;
  onContinue?: () => void;
  onBack: () => void;
  onViewWallet: () => void;
}

function continueLabel(returnTo: string): string {
  if (returnTo.startsWith('/configure/')) return 'Voltar pra iniciar a recarga';
  if (returnTo.startsWith('/amenities/')) return 'Voltar pro aspirador';
  // Aporte pelo aviso "sua recarga vai parar em breve" — a recarga segue rodando.
  if (returnTo.startsWith('/charging/')) return 'Voltar pra recarga';
  if (returnTo === '/wallet/cards') return 'Ver meus cartões';
  return 'Continuar onde parei';
}

export function PaidPhase({
  wallet,
  method,
  initialBalance,
  breakdown,
  returnTo,
  onContinue,
  onBack,
  onViewWallet
}: PaidPhaseProps) {
  // initialBalance pode ser null (fetch inicial falhou) — sem baseline não
  // dá pra afirmar o delta; o breakdown do PIX cobre a exibição.
  const delta =
    initialBalance !== null ? Number(wallet.balance_brl) - initialBalance : null;
  const hasCashback = breakdown !== null && breakdown.cashback > 0;
  const wn = walletNumbers(wallet);

  return (
    <>
      <div className="flex flex-col items-start gap-4 pt-2">
        <div
          className="flex h-[72px] w-[72px] items-center justify-center rounded-full text-white"
          style={{
            background: 'linear-gradient(135deg, #00C072 0%, #31B700 100%)',
            boxShadow: '0 12px 28px rgba(0,192,114,0.35)'
          }}
        >
          <CheckIconLarge />
        </div>
        <div>
          <Eyebrow>Pagamento recebido</Eyebrow>
          <Display size={36} className="mt-2 leading-tight">
            + {formatBRL(delta ?? breakdown?.pixCredit ?? 0)}
          </Display>
        </div>
      </div>

      {hasCashback && (
        <Card
          className="flex items-center gap-4 p-4"
          style={{
            background: 'rgba(192,76,174,0.05)',
            border: '1px solid rgba(192,76,174,0.28)'
          }}
        >
          <CashGlyphBadge size={48} />
          <div className="flex flex-1 flex-col">
            <span className="text-sm font-semibold" style={{ color: '#A23C92' }}>
              + {formatBRL(breakdown.cashback)} de cashback
            </span>
            {breakdown.cashbackPercent !== null && (
              <span className="text-xs text-bow-royal/65">
                {tierName(breakdown.cashbackPercent)} ·{' '}
                {breakdown.cashbackPercent}% sobre o PIX
              </span>
            )}
          </div>
        </Card>
      )}

      {/* Totais pós-pagamento. No cartão a Carteira cashback some (fator 0 —
          o total real de bônus é de PIX anteriores; mostrá-lo aqui confundiria);
          fica só o saldo creditado, coerente com o passo 2. O extrato completo
          está a um toque em "Ver saldo e extrato". */}
      {method === 'card' ? (
        <div className="flex-shrink-0 rounded-2xl bg-white px-4 py-3.5 shadow-soft">
          <span
            className="text-[10px] font-bold uppercase tracking-[0.08em]"
            style={{ color: '#00834D' }}
          >
            Entrou no saldo
          </span>
          <div
            className="mt-1"
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 600,
              fontSize: 22,
              letterSpacing: '-0.025em',
              lineHeight: 1.05,
              color: '#00834D'
            }}
          >
            {formatBRL(wn.balance)}
          </div>
        </div>
      ) : (
        <WalletResultCards
          saldoValue={formatBRL(wn.balance)}
          cashValue={formatBRL(wn.bonus)}
        />
      )}

      {returnTo && onContinue ? (
        <>
          {/* Fluxo "sem saldo → PIX → pago": devolve pro ponto onde parou. */}
          <Button onClick={onContinue}>{continueLabel(returnTo)}</Button>
          <Button variant="secondary" onClick={onViewWallet} size="md">
            Ver saldo e extrato
          </Button>
        </>
      ) : (
        <>
          <Button onClick={onViewWallet}>Ver saldo e extrato</Button>
          <Button variant="ghost" onClick={onBack} size="md">
            Voltar para o início
          </Button>
        </>
      )}
    </>
  );
}

function ExpiredPhase({ onRetry }: { onRetry: () => void }) {
  return (
    <>
      <Card className="flex flex-col gap-2 p-5">
        <span className="font-semibold">PIX expirado</span>
        <span className="text-sm text-bow-royal/65">
          A cobrança expirou sem confirmação. Gere um novo QR.
        </span>
      </Card>
      <Button onClick={onRetry}>Gerar novo PIX</Button>
      <Link
        to="/"
        className="inline-flex h-11 items-center justify-center text-sm font-medium text-bow-royal/65"
      >
        Voltar
      </Link>
    </>
  );
}

function extractPaymentBreakdown(
  txs: WalletTransaction[]
): { pixCredit: number; cashback: number; cashbackPercent: number | null } | null {
  const latestPix = txs.find((t) => t.type === 'credit_pix');
  if (!latestPix) return null;
  const pixTime = new Date(latestPix.created_at).getTime();
  const cashbackTx = txs.find(
    (t) =>
      t.type === 'cashback' &&
      Math.abs(new Date(t.created_at).getTime() - pixTime) < 5000
  );
  const pixCredit = Math.abs(Number(latestPix.amount_brl));
  const cashback = cashbackTx ? Math.abs(Number(cashbackTx.amount_brl)) : 0;
  const cashbackPercent =
    pixCredit > 0 && cashback > 0
      ? Math.round((cashback / pixCredit) * 100)
      : null;
  return { pixCredit, cashback, cashbackPercent };
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/* ============================================================
   Ícones
   ============================================================ */

function ChevronLeftIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}

/** Losangos PIX — marca do meio de pagamento nos tiles do passo 2. */
function PixDiamonds({ size = 34 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="#00A664"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 12 12 5l7 7-7 7-7-7z" />
      <path d="M5 12h3M16 12h3M12 5v3M12 16v3" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={34}
      height={34}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
      <path d="M2.5 10h19" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}

function CheckIconLarge() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="32"
      height="32"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}

/** Identidade nova do cashback: badge circular com `$` sobre gradiente roxo. */
function CashGlyphBadge({ size = 48 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="flex flex-shrink-0 items-center justify-center rounded-full text-white"
      style={{
        width: size,
        height: size,
        background: 'linear-gradient(135deg,#835DD0,#C04CAE)'
      }}
    >
      <span
        style={{
          fontFamily: 'Chillax, system-ui, sans-serif',
          fontWeight: 700,
          fontSize: size * 0.48,
          lineHeight: 1
        }}
      >
        $
      </span>
    </span>
  );
}
